import React, { useState } from 'react';
import { Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { useFileStore } from '../stores/file-store';
import FileList from '../components/files/FileList';
import ConfirmDialog from '../components/ConfirmDialog';
import { cn } from '../utils/cn';
import { useI18n } from '../i18n';

const TrashPage: React.FC = () => {
  const [isRestoring, setIsRestoring] = useState(false);
  const [isEmptyingTrash, setIsEmptyingTrash] = useState(false);
  const { files } = useFileStore();
  const { t } = useI18n();
  const [showConfirm, setShowConfirm] = useState(false);

  // Filter deleted files
  const deletedFiles = files.filter(file => file.deleted_at);

  const handleRestore = async (fileId: string) => {
    setIsRestoring(true);
    try {
      // Implement restore functionality
      console.log('Restoring file:', fileId);
    } catch (error) {
      console.error('Error restoring file:', error);
    } finally {
      setIsRestoring(false);
    }
  };

  const doEmptyTrash = async () => {
    setIsEmptyingTrash(true);
    try {
      // Implement empty trash functionality
      console.log('Emptying trash');
    } catch (error) {
      console.error('Error emptying trash:', error);
    } finally {
      setIsEmptyingTrash(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 mr-3 bg-red-50 text-red-600 rounded-md">
            <Trash2 size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{t('trashTitle')}</h1>
            <p className="text-sm text-gray-500">
              {t('trashAutoDelete')}
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setShowConfirm(true)}
            disabled={deletedFiles.length === 0 || isEmptyingTrash}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md",
              deletedFiles.length === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-red-600 text-white hover:bg-red-700"
            )}
          >
            {t('emptyTrash')}
          </button>
        </div>
      </div>

      {deletedFiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white border border-gray-200 rounded-lg">
          <div className="p-3 mb-4 bg-gray-100 text-gray-400 rounded-full">
            <Trash2 size={24} />
          </div>
          <h3 className="text-lg font-medium text-gray-900">{t('trashEmpty')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('noItemsInTrash')}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle size={16} className="mr-2 text-amber-500" />
                <span className="text-sm text-gray-600">
                  {t('autoDeleteNotice')}
                </span>
              </div>
              <button
                onClick={() => handleRestore('all')}
                disabled={isRestoring}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <RefreshCw size={14} className="mr-1.5" />
                {t('restoreAll')}
              </button>
            </div>
          </div>

          <FileList files={deletedFiles} />
        </div>
      )}
      <ConfirmDialog
        open={showConfirm}
        title="清空回收站"
        message={t('confirmEmptyTrash')}
        variant="danger"
        confirmText="清空"
        onConfirm={() => { setShowConfirm(false); doEmptyTrash(); }}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
};

export default TrashPage;